import {forwardRef, type ReactNode} from "react";
import {useRender} from "@base-ui/react/use-render";
import {cva, type VariantProps} from "class-variance-authority";
import {clsx} from "clsx";
import {FA6Icon} from "./FA6Icon";
import styles from "./Alert.module.css";

const alertVariants = cva(styles.root, {
	variants: {
		variant: {
			primary: styles.primary,
			success: styles.success,
			info: styles.info,
			warning: styles.warning,
			danger: styles.danger,
		},
		iconPosition: {
			left: styles.iconLeft,
			right: styles.iconRight,
		},
	},
	defaultVariants: {
		variant: "info",
	},
});

type AlertVariant = NonNullable<VariantProps<typeof alertVariants>["variant"]>;

const railIcons: Record<AlertVariant, string> = {
	primary: "square-list",
	success: "check",
	info: "info",
	warning: "triangle-exclamation",
	danger: "ban",
};

interface AlertProps extends useRender.ComponentProps<"div">,
	Omit<VariantProps<typeof alertVariants>, "iconPosition"> {
	/** Shows the coloured icon rail. Defaults to `"left"` when `icon` is set. */
	iconPosition?: "left" | "right"
	/** Replaces the rail's default glyph. */
	icon?: ReactNode
}

const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
	{className, render, variant = "info", iconPosition, icon, children, ...elementProps},
	forwardedRef,
) {
	const showIcon = iconPosition !== undefined || (icon !== undefined && icon !== null);
	return useRender({
		render,
		ref: forwardedRef,
		defaultTagName: "div",
		props: {
			"data-slot": "alert",
			role: "alert",
			className: clsx(
				alertVariants({variant, iconPosition: showIcon ? iconPosition ?? "left" : undefined}),
				className,
			),
			...elementProps,
			children: (
				<>
					{showIcon && (
						<span data-slot="alert-icon" className={styles.icon}>
							{icon ?? <FA6Icon icon={railIcons[variant]}/>}
						</span>
					)}
					{children}
				</>
			),
		},
	});
});

const AlertTitle = forwardRef<HTMLDivElement, useRender.ComponentProps<"div">>(
	function AlertTitle({className, render, ...elementProps}, forwardedRef) {
		return useRender({
			render,
			ref: forwardedRef,
			defaultTagName: "div",
			props: {
				"data-slot": "alert-title",
				className: clsx(styles.title, className),
				...elementProps,
			},
		});
	},
);

export {Alert, AlertTitle};
export type {AlertVariant};
